import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Store as StoreIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';

import { RootState } from '../store';
import { setCredentials } from '../store/slices/authSlice';
import { useLoginMutation } from '../services/auth.service';
import { useGetLocationsQuery } from '../services/locations.service';

interface LoginForm {
  email: string;
  password: string;
  locationId: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated, loading } = useSelector((state: RootState) => state.auth);
  
  const [login, { isLoading }] = useLoginMutation();
  const { data: locationsResponse, isLoading: locationsLoading, error: locationsError } = useGetLocationsQuery({ active: true });
  const [error, setError] = useState<string>('');

  const locations = locationsResponse?.data || [];

  // Debug logs
  React.useEffect(() => {
    console.log('Locations data:', locationsResponse);
    console.log('Locations loading:', locationsLoading);
    console.log('Locations error:', locationsError);
  }, [locationsResponse, locationsLoading, locationsError]);
  
  const { control, handleSubmit, formState: { errors } } = useForm<LoginForm>({
    defaultValues: {
      email: '',
      password: '',
      locationId: '',
    },
  });

  useEffect(() => {
    // Only redirect if we're truly authenticated and not in a loading state
    if (isAuthenticated && !loading) {
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location]);

  const onSubmit = async (data: LoginForm) => {
    try {
      setError('');
      const result = await login(data).unwrap();
      
      dispatch(setCredentials(result));
      toast.success('¡Bienvenido al sistema!');
      
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err: any) {
      const errorMessage = err?.data?.message || 'Error al iniciar sesión';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Verificando autenticación...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 3,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400, boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <StoreIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography component="h1" variant="h4" color="primary" fontWeight="bold">
                Plásticos Juticalpa
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Sistema de Inventario y Ventas
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'El email es requerido',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Email inválido',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    sx={{ mb: 2 }}
                    autoComplete="email"
                    autoFocus
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={{
                  required: 'La contraseña es requerida',
                  minLength: {
                    value: 6,
                    message: 'La contraseña debe tener al menos 6 caracteres',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Contraseña"
                    type="password"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    sx={{ mb: 2 }}
                    autoComplete="current-password"
                  />
                )}
              />

              <Controller
                name="locationId"
                control={control}
                rules={{
                  required: 'Selecciona una ubicación',
                }}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 3 }} error={!!errors.locationId}>
                    <InputLabel>
                      {locationsLoading ? 'Cargando ubicaciones...' : 'Ubicación'}
                    </InputLabel>
                    <Select {...field} label="Ubicación" disabled={locationsLoading}>
                      {locationsLoading && (
                        <MenuItem disabled>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Cargando ubicaciones...
                        </MenuItem>
                      )}
                      {locationsError && (
                        <MenuItem disabled>
                          Error al cargar ubicaciones
                        </MenuItem>
                      )}
                      {locations?.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.locationId && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.locationId.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ mb: 2, py: 1.5 }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>
            </Box>

            <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Usuarios de prueba:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Admin: admin@test.com / admin123<br />
                Gerente: gerente@test.com / manager123<br />
                Cajero: cajero1@test.com / cashier123
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default LoginPage;